<script lang="ts" setup>
const route = useRoute();
defineOptions({
  name: "DefaultLayout",
});

const bannerPromotion = ref<BannerPromotion>();

const fetchActiveBannerPromotion = async () => {
  try {
    bannerPromotion.value = await $fetch(`/api/banner-promotion/getPromotion`);
  } catch (e) {
    console.error(e);
    bannerPromotion.value = undefined;
  }
};

await fetchActiveBannerPromotion();

import basketEmpty from "assets/basket-empty.png";
import basketOne from "assets/basket-one.png";
import basketTwo from "assets/basket-two.png";
import basketThree from "assets/basket-three.png";
const basketImg = ref<string>(basketEmpty);

// Easter period check
const today = new Date();
const isEasterPeriod = computed(() => {
  const start = new Date("2025-04-17");
  const end = new Date("2025-04-28");
  return today >= start && today <= end;
});

const easterModalStart = useModal();
const easterModalEnd = useModal();

// Easter egg feature
const eggPositions = ["top-left", "top-right", "bottom-left"];
const eggPosition = ref(
  eggPositions[Math.floor(Math.random() * eggPositions.length)],
);
const collectedEggs = ref<number>(0);
const showEgg = ref<boolean>(false);
const shaking = ref<boolean>(false);
const easterGameAccepted = ref<boolean>(false);
const easterGameDeclined = ref<boolean>(false);
const easterGameDone = ref<boolean>(false);

watch(collectedEggs, () => {
  switch (collectedEggs.value) {
    case 1:
      basketImg.value = basketOne;
      break;
    case 2:
      basketImg.value = basketTwo;
      break;
    case 3:
      basketImg.value = basketThree;
      if (!easterGameDone.value && isEasterPeriod.value) easterModalEnd.open();
      break;
    default:
      basketImg.value = basketEmpty;
      break;
  }
});

const collectEgg = () => {
  if (collectedEggs.value < 3) {
    collectedEggs.value += 1;
    localStorage.setItem("collected-eggs", collectedEggs.value.toString());
    showEgg.value = false;
  }
};

function onEasterGameDeclined() {
  easterGameAccepted.value = false;
  easterGameDeclined.value = true;
  localStorage.setItem("easter-game-accepted", "false");
  // Store the decline flag in sessionStorage so it won't pop up again in this session.
  sessionStorage.setItem("easter-game-declined", "true");
  easterModalStart.close();
}

function onEasterGameAccepted() {
  easterGameAccepted.value = true;
  easterGameDeclined.value = false;
  localStorage.setItem("easter-game-accepted", "true");
  // Do not set the decline flag when the user accepts
  easterModalStart.close();
}

function onEasterGameDone() {
  easterGameDone.value = true;
  sessionStorage.setItem("easter-game-done", "true");
  easterModalEnd.close();
}

watch(
  () => route.path,
  () => {
    if (!isEasterPeriod.value || !import.meta.client) return;

    eggPosition.value =
      eggPositions[Math.floor(Math.random() * eggPositions.length)];
    collectedEggs.value = Number(localStorage.getItem("collected-eggs")) || 0;
    easterGameAccepted.value =
      localStorage.getItem("easter-game-accepted") === "true";
    // Only show the egg if the game is accepted and less than 3 eggs are collected.
    showEgg.value = collectedEggs.value < 3 && easterGameAccepted.value;
    easterGameDone.value =
      sessionStorage.getItem("easter-game-done") === "true";

    // Check if the user has already declined this session
    const gameDeclined =
      sessionStorage.getItem("easter-game-declined") === "true";
    // If game is not accepted and the user has not declined, then open the modal.
    if (!easterGameAccepted.value && !gameDeclined) {
      easterModalStart.open();
    }
  },
  { immediate: true },
);

onMounted(() => {
  setInterval(() => {
    if (isEasterPeriod.value && showEgg.value) {
      shaking.value = true;
      setTimeout(() => {
        shaking.value = false;
      }, 900);
    }
  }, 3000);
});
</script>

<template>
  <div class="min-h-dvh flex flex-col bg-white relative">
    <LayoutModalBackground />
    <LayoutHeader />
    <LayoutNotifications />
    <LayoutModalsContainer />
    <main>
      <slot />
    </main>

    <ClientOnly v-if="isEasterPeriod">
      <SharedModal
        :controller="easterModalStart"
        :on-close="onEasterGameDeclined"
      >
        <div class="relative">
          <img
            width="400"
            height="400"
            src="assets/easter-game-start.png"
            alt="oster-spiel"
          />
          <div
            class="w-80 flex justify-center gap-6 text-sm absolute bottom-16 left-1/2 -translate-x-1/2"
          >
            <button
              class="w-38 h-13 px-2 bg-scheppach-primary-500 text-white b b-r"
              @click="onEasterGameAccepted"
            >
              Jetzt Teilnehmen
            </button>
            <button
              class="w-38 h-13 px-2 bg-scheppach-primary-500 text-white b b-r"
              @click="onEasterGameDeclined"
            >
              Nein, ich möchte nicht sparen
            </button>
          </div>
        </div>
      </SharedModal>
    </ClientOnly>

    <ClientOnly v-if="isEasterPeriod">
      <SharedModal :controller="easterModalEnd" :on-close="onEasterGameDone">
        <div class="relative">
          <img
            width="440"
            height="40"
            src="assets/easter-game-end.png"
            alt="oster-spiel"
          />
          <div
            class="px-4 py-2 sm:px-6 sm:py-3 flex items-center justify-center font-bold rd-10 absolute top-38% left-1/2 -translate-x-1/2 bg-scheppach-primary-500 text-white b b-r"
          >
            Ostern15
          </div>
          <div
            class="px-4 py-2 sm:px-6 sm:py-3 flex items-center justify-center font-bold rd-10 absolute top-61% left-1/2 -translate-x-1/2 bg-scheppach-primary-500 text-white b b-r"
          >
            Ostern20
          </div>
          <NuxtLink
            class="w-full text-center text-white absolute top-76% sm:top-75% left-1/2 -translate-x-1/2 lh-tight text-3 sm:text-4"
            to="/Angebote/Osteraktion/"
            @click="onEasterGameDone"
          >
            Rabattcode kopieren, "hier" Produkt aussuchen und im Warenkorb
            einlösen!
          </NuxtLink>
        </div>
      </SharedModal>
    </ClientOnly>

    <template v-if="bannerPromotion">
      <LayerMiniDrawer
        v-if="bannerPromotion?.promotionStyling"
        :name="bannerPromotion.name"
        :code="bannerPromotion.code"
        :desc="bannerPromotion?.promotionStyling?.description"
        :textcolor="bannerPromotion.promotionStyling.textColour"
        :bgcolor="bannerPromotion.promotionStyling.backgroundColour"
        :icon="bannerPromotion.promotionStyling.icon"
      />
    </template>

    <!-- Easter Egg -->
    <img
      v-if="isEasterPeriod && showEgg"
      src="assets/easter-egg.png"
      alt="Easter Egg"
      class="easter-egg"
      :class="[eggPosition, { shake: shaking }]"
      @click="collectEgg"
    />
    <img
      v-if="isEasterPeriod && easterGameAccepted && !easterGameDone"
      :src="basketImg"
      alt="Easter Egg"
      class="basket bottom-right"
    />

    <LayoutFooter class="m-t-auto" />
  </div>
</template>

<style scoped>
.easter-egg {
  width: 100px;
  height: 100px;
  position: absolute;
  cursor: pointer;
  transition: transform 0.3s;
  z-index: 1;
}
.basket {
  width: 120px;
  height: 120px;
  position: fixed;
  cursor: pointer;
  transition: transform 0.3s;
  z-index: 1;
}
.easter-egg:hover {
  transform: scale(1.2);
}
.top-left {
  top: 140px;
  left: 10px;
}
.top-right {
  top: 140px;
  right: 10px;
}
.bottom-left {
  bottom: 550px;
  left: 120px;
}
.bottom-right {
  bottom: 20px;
  right: 30px;
}
/* Shake Animation */
@keyframes shake {
  0%,
  100% {
    transform: rotate(0deg);
  }
  20% {
    transform: rotate(-20deg);
  }
  40% {
    transform: rotate(20deg);
  }
  60% {
    transform: rotate(-20deg);
  }
  80% {
    transform: rotate(20deg);
  }
}

.shake {
  animation: shake 0.4s ease-in-out 3;
}
</style>
